import { Link, useSearchParams } from 'react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  formatCents,
  PAYMENT_FILTER_LABELS,
  PAYMENT_FILTERS,
  PAYMENT_STATUS_LABELS,
  PURCHASE_INVOICE_KIND_LABELS,
  PURCHASE_INVOICE_KINDS,
  PURCHASE_INVOICE_ORIGIN_LABELS,
  PURCHASE_INVOICE_ORIGINS,
  PURCHASE_INVOICE_STATUS_LABELS,
  PURCHASE_INVOICE_STATUSES,
  type Paginated,
  type PurchaseInvoiceSummary,
  type PurchaseOpenBalance,
} from '@kontor/shared';
import { api, queryString } from '../../api';
import { SortTh } from '../../components/SortTh';
import { ExpandRow, Summary } from '../../components/ExpandRow';
import { FilterBar, type FilterField } from '../../components/filter/FilterBar';
import { YearFilter, yearOf } from '../../components/YearFilter';
import { useSession } from '../../session';
import { invoiceDay } from './InvoicesPage';
import './invoices.css';
import './purchase.css';

/** Stand für die Anzeige: Entwurf, abgelehnt, bei freigegebenen Rechnungen der Zahlungsstand, überfällig hat Vorrang */
export function purchaseBadge(i: Pick<PurchaseInvoiceSummary, 'status' | 'paymentStatus' | 'overdue'>): { text: string; className: string } {
  if (i.status === 'draft') return { text: PURCHASE_INVOICE_STATUS_LABELS.draft, className: 'invoice-status' };
  if (i.status === 'rejected') return { text: PURCHASE_INVOICE_STATUS_LABELS.rejected, className: 'invoice-status invoice-status--cancelled' };
  if (i.paymentStatus === null) return { text: PURCHASE_INVOICE_STATUS_LABELS.approved, className: 'invoice-status invoice-status--issued' };
  if (i.overdue) return { text: 'Überfällig', className: 'invoice-status invoice-status--overdue' };
  return { text: PAYMENT_STATUS_LABELS[i.paymentStatus], className: `invoice-status invoice-status--${i.paymentStatus}` };
}

/** Liste der Eingangsrechnungen mit Suche und Filtern nach Stand, Zahlung, Art und Herkunft (KON-142) */
export function PurchaseInvoicesPage() {
  const [params, setParams] = useSearchParams();
  const { can } = useSession();
  const q = params.get('q') ?? '';
  const status = params.get('status') ?? '';
  const kind = params.get('kind') ?? '';
  const origin = params.get('origin') ?? '';
  const payment = params.get('payment') ?? '';
  // Vorgabe: das laufende Jahr
  const year = yearOf(params.get('jahr'));
  const sort = params.get('sort') ?? '';
  const page = Number(params.get('page') ?? 1);

  const list = useQuery({
    queryKey: ['purchase-invoices', { q, status, kind, origin, payment, year, page, sort }],
    queryFn: () => api<Paginated<PurchaseInvoiceSummary>>('GET', `/invoices-in${queryString({ q, status, kind, origin, payment, year, page, sort, perPage: 25 })}`),
    placeholderData: keepPreviousData,
  });
  const balance = useQuery({ queryKey: ['purchase-open-balance'], queryFn: async () => (await api<{ data: PurchaseOpenBalance }>('GET', '/invoices-in/open-balance')).data });
  const update = (next: Record<string, string>) => {
    const merged = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v) merged.set(k, v);
      else merged.delete(k);
    }
    if (!('page' in next)) merged.delete('page');
    setParams(merged);
  };
  const onSort = (next: string) => update({ sort: next });
  const all = [{ value: '', label: 'Alle' }];
  const fields: FilterField[] = [
    { key: 'status', label: 'Stand', type: 'select', options: [...all, ...PURCHASE_INVOICE_STATUSES.map((s) => ({ value: s, label: PURCHASE_INVOICE_STATUS_LABELS[s] }))] },
    { key: 'payment', label: 'Zahlung', type: 'select', options: [...all, ...PAYMENT_FILTERS.map((f) => ({ value: f, label: PAYMENT_FILTER_LABELS[f] }))] },
    { key: 'kind', label: 'Art', type: 'select', options: [...all, ...PURCHASE_INVOICE_KINDS.map((k) => ({ value: k, label: PURCHASE_INVOICE_KIND_LABELS[k] }))] },
    { key: 'origin', label: 'Herkunft', type: 'select', options: [...all, ...PURCHASE_INVOICE_ORIGINS.map((o) => ({ value: o, label: PURCHASE_INVOICE_ORIGIN_LABELS[o] }))] },
  ];
  const title = (i: PurchaseInvoiceSummary) => i.supplierInvoiceNumber ?? 'Ohne Nummer';

  return (
    <>
      <div className="page-head">
        <h1>Eingangsrechnungen</h1>
        <span className="muted">{list.data ? `${list.data.meta.total} insgesamt` : ''}</span>
        {can('invoices_in.write') && <Link className="btn btn--primary btn--sm page-head__action" to="/eingangsrechnungen/neu">Neue Eingangsrechnung</Link>}
      </div>
      {balance.data && (
        <p className="invoice-balance">
          <strong>Offene Verbindlichkeiten:</strong> {balance.data.count === 0 ? 'keine' : `${balance.data.count} Rechnung${balance.data.count === 1 ? '' : 'en'} über ${formatCents(balance.data.openCents)}`}
          {balance.data.overdueCount > 0 && (
            <>
              {' · '}
              <button type="button" className="link-button invoice-balance__late" onClick={() => update({ payment: 'overdue' })}>
                davon überfällig {formatCents(balance.data.overdueCents)} ({balance.data.overdueCount})
              </button>
            </>
          )}
          {balance.data.draftCount > 0 && (
            <>
              {' · '}
              <button type="button" className="link-button" onClick={() => update({ status: 'draft' })}>
                {balance.data.draftCount} Entwurf{balance.data.draftCount === 1 ? '' : 'e'} zu prüfen
              </button>
            </>
          )}
        </p>
      )}
      <div className="list-with-year">
        <div className="list-with-year__main">
          <FilterBar
            search={{ value: q, placeholder: 'Rechnungsnummer, Lieferant oder Notiz', onSubmit: (v) => update({ q: v }) }}
            fields={fields}
            values={{ status, payment, kind, origin }}
            onChange={(key, value) => update({ [key]: value })}
            onReset={() => setParams(new URLSearchParams())}
          />
        </div>
        <YearFilter endpoint="/invoices-in/years" queryKey="purchase-invoices" value={params.get('jahr')} onChange={(v) => update({ jahr: v })} />
      </div>
      {list.isError && <div className="alert alert--error">{(list.error as Error).message}</div>}
      <div className="card table-card">
        <table className="table table--cards">
          <thead>
            <tr>
              <SortTh label="Rechnung" sortKey="number" sort={sort} onSort={onSort} />
              <SortTh label="Lieferant" sortKey="supplier" sort={sort} onSort={onSort} />
              <SortTh label="Art" sortKey="kind" sort={sort} onSort={onSort} />
              <SortTh label="Stand" sortKey="status" sort={sort} onSort={onSort} />
              <SortTh label="Datum" sortKey="invoiceDate" sort={sort} onSort={onSort} first="desc" />
              <SortTh label="Fällig" sortKey="dueDate" sort={sort} onSort={onSort} />
              <SortTh label="Brutto" sortKey="gross" sort={sort} onSort={onSort} first="desc" className="num" />
              <SortTh label="Offen" sortKey="open" sort={sort} onSort={onSort} first="desc" className="num" />
            </tr>
          </thead>
          <tbody>
            {list.isLoading && <tr><td colSpan={8} className="muted">Wird geladen …</td></tr>}
            {list.data?.data.length === 0 && (
              <tr><td colSpan={8} className="empty">{q || status || kind || payment || origin ? 'Keine Eingangsrechnungen zu dieser Suche.' : year ? `Keine Eingangsrechnungen im Jahr ${year}. Über „Jahr“ lässt sich ein anderes Jahr wählen.` : 'Noch keine Eingangsrechnungen. Legen Sie eine über „Neue Eingangsrechnung“ an und importieren Sie die Datei des Lieferanten.'}</td></tr>
            )}
            {list.data?.data.map((i) => {
              const badge = purchaseBadge(i);
              return (
                <ExpandRow
                  key={i.id}
                  summary={
                    <Summary
                      title={<Link to={`/eingangsrechnungen/${i.id}`} className="strong-link">{title(i)}</Link>}
                      aside={<span className={badge.className}>{badge.text}</span>}
                      line2={i.supplierName ?? 'Lieferant fehlt'}
                      line3={[PURCHASE_INVOICE_KIND_LABELS[i.kind], i.grossCents ? formatCents(i.grossCents) : null, i.openCents ? `offen ${formatCents(i.openCents)}` : null].filter(Boolean).join(' · ')}
                    />
                  }
                >
                  <td className="cell-desktop-title">
                    <Link to={`/eingangsrechnungen/${i.id}`} className="strong-link">{title(i)}</Link>
                    <div className="muted small">{PURCHASE_INVOICE_ORIGIN_LABELS[i.origin]}</div>
                  </td>
                  <td data-label="Lieferant">{i.supplierContactId ? <Link to={`/kontakte/${i.supplierContactId}`}>{i.supplierName}</Link> : (i.supplierName ?? '–')}</td>
                  <td data-label="Art">{PURCHASE_INVOICE_KIND_LABELS[i.kind]}</td>
                  <td data-label="Stand"><span className={badge.className}>{badge.text}</span></td>
                  <td data-label="Datum">{invoiceDay(i.invoiceDate)}</td>
                  <td data-label="Fällig">{invoiceDay(i.dueDate)}</td>
                  <td data-label="Brutto" className="num">{i.grossCents ? formatCents(i.grossCents) : '–'}</td>
                  <td data-label="Offen" className="num">{i.openCents === null ? '–' : formatCents(i.openCents)}</td>
                </ExpandRow>
              );
            })}
          </tbody>
        </table>
      </div>
      {list.data && list.data.meta.lastPage > 1 && (
        <div className="pager">
          <button type="button" className="btn btn--secondary btn--sm" disabled={page <= 1} onClick={() => update({ page: String(page - 1) })}>Zurück</button>
          <span className="muted">Seite {page} von {list.data.meta.lastPage}</span>
          <button type="button" className="btn btn--secondary btn--sm" disabled={page >= list.data.meta.lastPage} onClick={() => update({ page: String(page + 1) })}>Weiter</button>
        </div>
      )}
    </>
  );
}
